<template>
  <div
    v-if="complete"
    class="absolute top-0 right-0 bottom-0 left-0"
    :class="{'lg:-z-10': isModalOpen, '-z-10': !isModalOpen}">
    <div class="relative">
      <div
        class="absolute top-0 right-0 bottom-0 left-0 transition-all duration-300 ease-out"
        :class="{'bg-black opacity-75': isModalOpen, 'bg-overlay-black': !isModalOpen}"></div>
      <img :src="tour.background_image.url | getImagePath"
        :alt="tour.background_image.name"
        loading="lazy"
        class="object-cover min-h-screen lg:max-h-screen w-full">
      <div class="absolute top-0 right-0 bottom-0 left-0 mt-16 mx-6 md:m-16 flex items-center">
        <div class="text-white text-left">
          <ebr-back-button />
          <div class="flex md:items-center justify-center md:justify-start font-light my-4">
            <ebr-tour-orologio
              :containerClass="'flex items-center'"
              :content="tour.info.duration" />
            <ebr-span :content="'|'" class="mx-1 md:mx-4" />
            <ebr-tour-prezzo
              :containerClass="'flex items-center'"
              :content="tour.info.price" />
            <ebr-span :content="'|'" class="mx-1 md:mx-4" />
            <ebr-tour-tappe
              :containerClass="'flex items-center'"
              :content="tour.info.number_of_stops" />
          </div>
          <ebr-h1
            :content="tour.info.title"
            :classes="'text-center md:text-left text-4xl md:text-6xl font-normal'"/>
          <ebr-p
            :content="tour.info.description"
            :classes="'text-center md:text-left w-full md:w-2/3 lg:w-1/2'"/>
          <div class="mt-5 flex flex-col md:flex-row items-center justify-center md:justify-start">
            <ebr-round-button-link
            :label="$t('shared.strings.bookNow')"
            :extraClasses="'text-xs md:text-sm lg:text-md \
            bg-white text-black hover:bg-gray-200 mb-4 md:mb-0'"
            href="tel:+393319787787" />
            <ebr-text-link
              :label="$t('shared.strings.moreInfo')"
              :classes="'text-xs md:text-sm lg:text-md lg:ml-10'"
              @click.native="tabChange('description')" />
          </div>
        </div>
      </div>
      <div
        class="absolute inset-0 md:top-auto md:right-auto overflow-y-auto hide-scrollbar w-full
          lg:rounded-tr-lg bg-white lg:w-2/3 z-10 transition-all duration-300 ease-in-out px-6"
          :class="{'hidden lg:block lg:h-24': !isModalOpen, 'block h-full lg:h-144': isModalOpen}">
        <div class="grid grid-cols-4 md:grid-cols-5 lg:h-24 mb-5 md:mb-0 pt-4 md:pt-0">
          <div class="flex items-center justify-center">
            <ebr-text-link
              :label="$t('tour_detail.description')"
              :classes="'text-xs md:text-sm lg:text-md lg:ml-10'"
              @click.native="tabChange('description')"/>
          </div>
          <div class="flex items-center justify-center text-center">
            <ebr-text-link
              :label="$t('tour_detail.included_services.link')"
              :classes="'text-xs md:text-sm lg:text-md lg:ml-10'"
              @click.native="tabChange('photo')"/>
          </div>
          <div class="flex items-center justify-center lg:col-span-2">
            <ebr-round-button-link
              :label="$t('tour_detail.booking.link')"
              :extraClasses="'text-xs md:text-sm lg:text-md bg-black text-white hover:bg-gray-700 \
              lg:w-2/3'"
              href="tel:+393319787787"/>
          </div>
          <div class="flex items-start justify-end md:pt-4 md:pr-6" v-if="isModalOpen">
            <a class="text-2xl cursor-pointer" @click.prevent="closeModal()">&times;</a>
          </div>
        </div>
        <div v-if="isModalOpen">
          <component :is="tabComponent" :tour="tour"></component>
        </div>
      </div>
      <div
        class="absolute hidden lg:block inset-0 md:top-auto md:left-auto
          overflow-y-auto hide-scrollbar md:w-1/4 z-40 pb-10 pr-10"
        v-if="tour.up_next_tour !== null">
        <div class="relative bg-white rounded-lg w-full h-80
          flex flex-col justify-end items-center">
          <img :src="tour.up_next_tour.slider_image.url | getImagePath"
            :alt="tour.up_next_tour.slider_image.name"
            loading="lazy"
            class="object-cover w-full h-full rounded-lg">
          <div
            class="absolute top-0 right-0 bottom-0 left-0 bg-overlay-black rounded-lg"></div>
          <div
            class="absolute top-0 right-0 bottom-0 left-0 rounded-lg z-10 m-4">
            <div class="absolute top-0 left-0 flex items-center text-white">
              {{$t('tour_detail.up_next')}}
            </div>
            <div class="absolute top-0 right-0 flex items-center text-white">
              {{tour.up_next_tour[`information_${this.language}`].duration}}
              <svg class="h-4 w-4 stroke-current ml-3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g fill="none" stroke-miterlimit="10" transform="translate(.5 .5)"><circle cx="10.047" cy="10.047" r="10.047"/><path d="M10.047 0v3.257M10.047 16.837v3.257M20.094 10.047h-3.257M3.257 10.047H0M13.668 6.425l-3.621 3.621 4.64 4.64"/></g>
              </svg>
            </div>
            <div class="absolute bottom-0 left-0 text-white flex flex-col">
              <span class="text-2xl font-regular mb-2 text-left">
                {{tour.up_next_tour[`information_${this.language}`].title}}
              </span>
              <span class="text-sm text-left">
                {{tour.up_next_tour[`information_${this.language}`].price}}
              </span>
            </div>
            <div class="absolute bottom-0 right-0 -mr-10 mb-10 bg-white
              flex justify-center items-center rounded-full w-12 h-12">
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@enrian/vue-pikaday';
import '@enrian/vue-pikaday/dist/vue-pikaday.min.css';
import moment from 'moment';
import axios from 'axios';

import BackButtonVue from '../components/atoms/buttons/BackButton.vue';
import SpanVue from '../components/atoms/typography/Span.vue';
import IconTextOrologioVue from '../components/atoms/icons-with-text/IconTextOrologio.vue';
import IconTextPrezzoVue from '../components/atoms/icons-with-text/IconTextPrezzo.vue';
import IconTextTappeVue from '../components/atoms/icons-with-text/IconTextTappe.vue';
import H1Vue from '../components/atoms/typography/H1.vue';
import ParagraphVue from '../components/atoms/typography/Paragraph.vue';
import RoundButtonLinkVue from '../components/atoms/buttons/RoundButtonLink.vue';
import TextLinkVue from '../components/atoms/buttons/TextLink.vue';
import DescriptionTabVue from '../components/organism/tour/DescriptionTab.vue';
import InformationTabVue from '../components/organism/tour/InformationTab.vue';
import PriceTabVue from '../components/organism/tour/PriceTab.vue';
import H2Vue from '../components/atoms/typography/H2.vue';

export default {
  data() {
    return {
      complete: false,
      tour: {},
      tabComponent: 'ebr-tab-description',
      now: null,
      pikadayOptions: {
        format: 'DD MMMM YYYY',
        minDate: moment().toDate(),
        value: moment().toDate(),
      },
    };
  },
  computed: {
    isModalOpen() {
      return this.$store.state.isModalOpen;
    },
    isPriceChecked() {
      return this.$store.state.isPriceChecked;
    },
    language() {
      return this.$store.state.language;
    },
  },
  methods: {
    fetchTour() {
      axios.get(`/tours?slug=${this.$route.params.slug}`)
        .then((response) => {
          this.setTour(response.data[0]);
          this.complete = true;
        });
    },
    setTour(tour) {
      this.tour = tour;
      this.tour.info = tour[`information_${this.language}`];
    },
    closeModal() {
      if (this.isModalOpen) {
        this.$store.commit('closeModal');
      }
    },
    tabChange(component) {
      if (!this.isModalOpen) {
        this.$store.commit('toggleModal');
      }
      this.tabComponent = `ebr-tab-${component}`;
    },
    closeDateSelect() {
      this.$store.commit('togglePriceChecked');
    },
    applyStyle() {
      return this.isModalOpen && this.isPriceChecked;
    },
  },
  beforeMount() {
    this.fetchTour();
  },
  watch: {
    language() {
      this.fetchTour();
    },
  },
  components: {
    'ebr-back-button': BackButtonVue,
    'ebr-tour-orologio': IconTextOrologioVue,
    'ebr-tour-prezzo': IconTextPrezzoVue,
    'ebr-tour-tappe': IconTextTappeVue,
    'ebr-span': SpanVue,
    'ebr-h1': H1Vue,
    'ebr-h2': H2Vue,
    'ebr-p': ParagraphVue,
    'ebr-round-button-link': RoundButtonLinkVue,
    'ebr-text-link': TextLinkVue,
    'ebr-tab-description': DescriptionTabVue,
    'ebr-tab-photo': InformationTabVue,
    'ebr-tab-price': PriceTabVue,
  },
};
</script>

<style>

</style>
